// 3MF
import {
    type Matrix3d,
    ThreeMfBase,
    ThreeMfBaseMaterials,
    ThreeMfBuild,
    ThreeMfComponent,
    ThreeMfComponents,
    ThreeMfItem,
    ThreeMfMesh,
    ThreeMfMeta,
    ThreeMfModel,
    ThreeMfObject,
    ThreeMfResources,
    ThreeMfTriangle,
    ThreeMfTriangles,
    ThreeMfVertex,
    ThreeMfVertices,
} from "./3mf";
import {
    type I3mfBaseMaterials,
    type I3mfComponents,
    type I3mfMesh,
    type I3mfMetadata,
    type I3mfModel,
    type I3mfObject,
    type I3mfTriangle,
    type I3mfTriangles,
    type I3mfVertex,
    type I3mfVertices,
    type ST_ResourceID,
    type ST_ResourceIndex,
    type ST_Unit,
    ST_ObjectType,
} from "./3mf.interfaces";
import { ThreeMfContentType, ThreeMfContentTypes, ThreeMfDocument, ThreeMfRelationship, ThreeMfRelationships } from "./3mf.opc";
import {
    type I3mfDocument,
    Known3mfRelationshipTypes,
    KnownI3mfContentType,
    ModelFileName,
    Object3dDirName,
    type I3mfContentType,
    type I3mfContentTypes,
    type I3mfRelationship,
    type I3mfRelationships,
} from "./3mf.opc.interfaces";
import { type I3mfRGBAColor, type I3mfVertexData, type ThreeMfFloatArray, type ThreeMfIndicesArray } from "./3mf.types";

/**
 * Handler invoked for every vertex generated by a {@link ThreeMfMeshBuilder}, allowing it to be transformed before export.
 */
export type VertexHandler = (vertex: I3mfVertex) => I3mfVertex;
/**
 * Handler invoked for every triangle generated by a {@link ThreeMfMeshBuilder}, allowing it to be transformed before export.
 */
export type TriangleHandler = (triangle: I3mfTriangle) => I3mfTriangle;

/**
 * Fluent builder for a 3MF object resource.
 * An object is the base resource of a 3MF model and is either a mesh or a set of components.
 */
export class ThreeMfObjectBuilder {
    /**
     * The 3MF object resource currently being built.
     */
    protected _object: ThreeMfObject;

    /**
     * Creates a new object builder.
     * @param id The unique resource id of the object within the model.
     * @param type The 3MF object type (model, support, solidsupport, surface or other).
     */
    public constructor(id: ST_ResourceID, type: ST_ObjectType) {
        this._object = new ThreeMfObject(id, type);
    }

    /**
     * Sets the human readable name of the object.
     * @param name The name to assign to the object.
     * @returns This builder, to allow chaining.
     */
    public withName(name: string): ThreeMfObjectBuilder {
        this._object.name = name;
        return this;
    }

    /**
     * Sets the thumbnail of the object.
     * @param thumbnail The package relative path of the thumbnail part.
     * @returns This builder, to allow chaining.
     */
    public withThumbnail(thumbnail: string): ThreeMfObjectBuilder {
        this._object.thumbnail = thumbnail;
        return this;
    }

    /**
     * Assigns the property resource used by the object.
     * @param id The resource id of the property group (for example a base materials group).
     * @param index The zero based index of the property within the group. Defaults to 0.
     * @returns This builder, to allow chaining.
     */
    public withProperty(id: ST_ResourceIndex, index: number = 0): ThreeMfObjectBuilder {
        this._object.pid = id;
        this._object.id = index;
        return this;
    }

    /**
     * Returns the object that has been built.
     * @returns The built 3MF object resource.
     */
    public build(): I3mfObject {
        return this._object;
    }

    /**
     * Discards the current object and starts building a new one.
     * @param id The unique resource id of the new object.
     * @param type The 3MF object type of the new object.
     */
    public reset(id: ST_ResourceID, type: ST_ObjectType) {
        this._object = new ThreeMfObject(id, type);
    }
}

/**
 * Fluent builder for a 3MF object made of components.
 * A components object references other objects instead of defining its own geometry.
 */
export class ThreeMfComponentsBuilder extends ThreeMfObjectBuilder {
    /**
     * Creates a new components object builder.
     * @param id The unique resource id of the object within the model.
     * @param type The 3MF object type. Defaults to `ST_ObjectType.model`.
     */
    public constructor(id: ST_ResourceID, type: ST_ObjectType = ST_ObjectType.model) {
        super(id, type);
        this._object.content = new ThreeMfComponents();
    }

    /**
     * Adds a component referencing another object resource.
     * @param id The resource id of the referenced object.
     * @param t The optional transform applied to the referenced object.
     * @returns This builder, to allow chaining.
     */
    public withComponent(id: ST_ResourceID, t?: Matrix3d): ThreeMfComponentsBuilder {
        (this._object.content as I3mfComponents).component.push(new ThreeMfComponent(id, t));
        return this;
    }
}

/**
 * Fluent builder for a 3MF object holding a mesh.
 * Vertices and triangles are generated from Babylon vertex data and can be post processed through handlers.
 */
export class ThreeMfMeshBuilder extends ThreeMfObjectBuilder {
    /**
     * Optional handler invoked for every vertex before it is added to the mesh.
     */
    _vh?: VertexHandler;
    /**
     * Optional handler invoked for every triangle before it is added to the mesh.
     */
    _th?: TriangleHandler;

    /**
     * Creates a new mesh object builder.
     * @param id The unique resource id of the object within the model.
     */
    public constructor(id: ST_ResourceID) {
        super(id, ST_ObjectType.model);
    }

    /**
     * Registers the handlers used to post process the generated geometry.
     * @param vertex The handler invoked for every generated vertex.
     * @param triangle The optional handler invoked for every generated triangle.
     * @returns This builder, to allow chaining.
     */
    public withPostProcessHandlers(vertex: VertexHandler, triangle?: TriangleHandler): ThreeMfMeshBuilder {
        this._vh = vertex;
        this._th = triangle;
        return this;
    }

    /**
     * Builds the mesh content of the object from the provided vertex data.
     * @param data The positions and indices used to generate the mesh.
     * @returns This builder, to allow chaining.
     */
    withData(data: I3mfVertexData): ThreeMfMeshBuilder {
        this._object.content = this._buildMesh(data);
        return this;
    }

    /**
     * Assigns the material used by the mesh.
     * @param id The resource id of the base materials group.
     * @param i The zero based index of the material within the group.
     * @returns This builder, to allow chaining.
     */
    withMaterial(id: ST_ResourceID, i: number): ThreeMfMeshBuilder {
        this._object.pid = id;
        this._object.pindex = i;
        return this;
    }

    /**
     * Converts vertex data into a 3MF mesh.
     * @param data The positions and indices used to generate the mesh.
     * @returns The generated 3MF mesh.
     */
    private _buildMesh(data: I3mfVertexData): I3mfMesh {
        const vertices = this._buildVertices(data.positions);
        const triangles = this._buildTriangle(data.indices);
        return new ThreeMfMesh(vertices, triangles);
    }

    /**
     * Converts a flat position array into a 3MF vertex list, applying the vertex handler when one is registered.
     * @param p The flat array of x, y and z positions.
     * @returns The generated 3MF vertex list.
     */
    private _buildVertices(p: ThreeMfFloatArray | null): I3mfVertices {
        const container = new ThreeMfVertices();
        if (p) {
            for (let i = 0; i < p.length;) {
                const x = p[i++];
                const y = p[i++];
                const z = p[i++];
                let v = new ThreeMfVertex(x, y, z);
                // might be optimized....
                if (this._vh) {
                    v = this._vh(v);
                }
                container.vertex.push(v);
            }
        }
        return container;
    }

    /**
     * Converts a flat index array into a 3MF triangle list, applying the triangle handler when one is registered.
     * @param indice The flat array of vertex indices, three per triangle.
     * @returns The generated 3MF triangle list.
     */
    private _buildTriangle(indice: ThreeMfIndicesArray | null): I3mfTriangles {
        const container = new ThreeMfTriangles();
        if (indice) {
            for (let i = 0; i < indice.length;) {
                const a = indice[i++];
                const b = indice[i++];
                const c = indice[i++];
                let t = new ThreeMfTriangle(a, b, c);
                // might be optimized....
                if (this._th) {
                    t = this._th(t);
                }
                container.triangle.push(t);
            }
        }
        return container;
    }
}

/**
 * Fluent builder for a 3MF base materials resource.
 * Colors are stored as sRGB hexadecimal strings as required by the 3MF specification.
 */
export class ThreeMfMaterialBuilder {
    private _m: I3mfBaseMaterials;

    /**
     * Creates a new base materials builder.
     * @param id The unique resource id of the base materials group within the model.
     */
    public constructor(id: ST_ResourceID) {
        this._m = new ThreeMfBaseMaterials(id);
    }

    /**
     * Adds a named color to the group, or updates it when the name already exists.
     * @param name The name of the material. The lookup is case insensitive.
     * @param color The linear RGBA color, converted to an sRGB hexadecimal string.
     * @returns This builder, to allow chaining.
     */
    public withColor(name: string, color: I3mfRGBAColor): ThreeMfMaterialBuilder {
        this._m.base = this._m.base ?? [];
        let m = this._m.base.find((m) => m.name.toLowerCase() === name.toLowerCase());
        if (m) {
            m.displaycolor = this._rgbaToHex(color);
            return this;
        }
        m = new ThreeMfBase(name, this._rgbaToHex(color));
        this._m.base.push(m);
        return this;
    }

    /**
     * Returns the base materials group that has been built.
     * @returns The built base materials resource.
     */
    public build(): I3mfBaseMaterials {
        return this._m;
    }

    /**
     * Converts a linear color into the sRGB hexadecimal string used by 3MF.
     * @param c The linear color to convert. The alpha channel is optional.
     * @returns The color as `#RRGGBB`, or `#RRGGBBAA` when an alpha channel is provided.
     */
    private _rgbaToHex(c: I3mfRGBAColor | { r: number; g: number; b: number; a?: number }): string {
        const toSRGB = (c: number) => Math.round(Math.min(255, Math.max(0, Math.pow(c, 1 / 2.2) * 255)));

        const r = toSRGB(c.r).toString(16).padStart(2, "0").toUpperCase();
        const g = toSRGB(c.g).toString(16).padStart(2, "0").toUpperCase();
        const b = toSRGB(c.b).toString(16).padStart(2, "0").toUpperCase();

        if (typeof (c as any).a === "number") {
            const a = Math.round(Math.min(255, Math.max(0, c.a! * 255)))
                .toString(16)
                .padStart(2, "0")
                .toUpperCase();
            return `#${r}${g}${b}${a}`;
        }

        return `#${r}${g}${b}`;
    }
}

/**
 * Fluent builder for a 3MF model, the root part of a 3MF document.
 * It aggregates the resources (materials, meshes and components) and the build items that reference them.
 */
export class ThreeMfModelBuilder {
    /**
     * The lower cased set of the metadata names defined by the 3MF specification.
     */
    static KnownMetaSet = new Set(ThreeMfModel.KnownMeta.map((m) => m.toLowerCase()));

    /**
     * The 3MF model currently being built.
     */
    _model: ThreeMfModel = new ThreeMfModel();
    /**
     * The object resources added to the model, indexed by resource id.
     */
    _objects = new Map<string, I3mfObject>();

    /**
     * Adds a metadata entry to the model.
     * @param name The metadata name. Names outside of the 3MF specification must be namespace qualified.
     * @param value The metadata value.
     * @param preserve Whether consumers must preserve this entry when editing the document.
     * @param type The XML schema type of the value. Defaults to `xs:string`.
     * @returns This builder, to allow chaining.
     */
    public withMetaData(name: string, value: string, preserve?: boolean, type?: string): ThreeMfModelBuilder {
        if (!this._model.metadata) {
            // lazzy
            this._model.metadata = new Array<I3mfMetadata>();
        }
        //const isKnownMeta = (s: string): boolean => TMFBuilder.knownMetaSet.has(s.toLowerCase());
        //const qn:IQName = xmlNameToParts(name);
        this._model.metadata.push(new ThreeMfMeta(name, value, preserve, type));
        return this;
    }

    /**
     * Adds a base materials group to the model resources.
     * @param material The base materials resource, or a builder that produces one.
     * @returns This builder, to allow chaining.
     */
    public withMaterial(material: I3mfBaseMaterials | ThreeMfMaterialBuilder): ThreeMfModelBuilder {
        if (material instanceof ThreeMfMaterialBuilder) {
            material = material.build();
        }
        if (material) {
            this._model.resources = this._model.resources ?? new ThreeMfResources();
            this._model.resources.basematerials = this._model.resources.basematerials ?? [];
            this._model.resources.basematerials.push(material);
        }
        return this;
    }

    /**
     * Adds a mesh object to the model resources.
     * @param object The object resource, or a builder that produces one.
     * @returns This builder, to allow chaining.
     */
    public withMesh(object: I3mfObject | ThreeMfMeshBuilder): ThreeMfModelBuilder {
        if (object instanceof ThreeMfMeshBuilder) {
            object = object.build();
        }
        this._model.resources = this._model.resources ?? new ThreeMfResources();
        this._model.resources.object.push(object);
        return this;
    }

    /**
     * Adds a components object to the model resources.
     * @param components The object resource, or a builder that produces one.
     * @returns This builder, to allow chaining.
     */
    public withComponents(components: I3mfObject | ThreeMfComponentsBuilder): ThreeMfModelBuilder {
        if (components instanceof ThreeMfComponentsBuilder) {
            components = components.build();
        }
        this._model.resources = this._model.resources ?? new ThreeMfResources();
        this._model.resources.object.push(components);
        return this;
    }

    /**
     * Adds a build item, which places an object resource in the build plate.
     * @param objectid The resource id of the object to place.
     * @param transform The optional transform applied to the object.
     * @param partnumber The optional part number identifying the produced part.
     * @returns This builder, to allow chaining.
     */
    public withBuild(objectid: ST_ResourceID, transform?: Matrix3d, partnumber?: string): ThreeMfModelBuilder {
        this._model.build = this._model.build ?? new ThreeMfBuild();
        this._model.build.item?.push(new ThreeMfItem(objectid, transform, partnumber));
        return this;
    }

    /**
     * Sets the unit in which the model coordinates are expressed.
     * @param unit The 3MF unit (micron, millimeter, centimeter, inch, foot or meter).
     * @returns This builder, to allow chaining.
     */
    public withUnit(unit: ST_Unit): ThreeMfModelBuilder {
        this._model.unit = unit;
        return this;
    }

    /**
     * Discards the current model and starts building a new one.
     * @returns This builder, to allow chaining.
     */
    public reset(): ThreeMfModelBuilder {
        this._model = new ThreeMfModel();
        this._objects = new Map<string, I3mfObject>();
        return this;
    }

    /**
     * Validates and returns the model that has been built.
     * @returns The built 3MF model.
     * @throws When the model has no object resource or no build item.
     */
    public build(): ThreeMfModel {
        // quick surface check..
        if (!this._model.resources?.object?.length) {
            throw new Error("Invalid state: resources MUST be defined ");
        }
        if (!this._model.build?.item?.length) {
            throw new Error("Invalid state: Build MUST be defined ");
        }
        return this._model;
    }
}

/**
 * Fluent builder for a 3MF document, the OPC package that wraps the model.
 * It gathers the content types, the relationships and the model part.
 */
export class ThreeMfDocumentBuilder {
    private _cts?: I3mfContentTypes;
    private _rs?: I3mfRelationships;
    private _m?: I3mfModel;

    /**
     * Declares an OPC content type. Duplicate declarations are ignored.
     * @param type The extension to content type mapping to declare.
     * @returns This builder, to allow chaining.
     */
    public withContentType(type: I3mfContentType): ThreeMfDocumentBuilder {
        if (!this._cts) {
            this._cts = new ThreeMfContentTypes();
        }
        const arr = this._cts.items;
        if (!arr.some((x) => x.ext === type.ext && x.ct === type.ct)) {
            arr.push(type);
        }
        return this;
    }

    /**
     * Adds an OPC relationship and ensures the relationships content type is declared.
     * Relationships with an already registered id are ignored.
     * @param rel The relationship to add.
     * @returns This builder, to allow chaining.
     */
    public withRelationship(rel: I3mfRelationship): ThreeMfDocumentBuilder {
        if (!this._rs) {
            this._rs = new ThreeMfRelationships();
        }
        const arr = this._rs.items;
        if (!arr.some((x) => x.id === rel.id)) {
            arr.push(rel);
        }
        // here we ensure that the content type is declared.
        this.withContentType(new ThreeMfContentType("rels", KnownI3mfContentType.Relationships));
        return this;
    }

    /**
     * Sets the model part of the document and ensures the model content type is declared.
     * @param m The model resource, or a builder that produces one.
     * @returns This builder, to allow chaining.
     */
    public withModel(m: I3mfModel | ThreeMfModelBuilder): ThreeMfDocumentBuilder {
        if (m instanceof ThreeMfModelBuilder) {
            m = m.build();
        }
        this._m = m;
        // here we ensure that the content type is declared.
        this.withContentType(new ThreeMfContentType("model", KnownI3mfContentType.Model));
        return this;
    }

    /**
     * Validates and returns the document that has been built.
     * A default relationship pointing at the model part is generated when none was provided.
     * @returns The built 3MF document.
     * @throws When no model has been set.
     */
    public build(): I3mfDocument {
        if (!this._m) {
            throw new Error("Invalid state: you Must provide at least a model.");
        }

        // we automate the build of the relationship if not provided..
        const path = `${Object3dDirName}${ModelFileName}`;
        if (!this._rs) {
            const absolutePath = `/${path}`;
            this.withRelationship(new ThreeMfRelationship(`rel${0}`, Known3mfRelationshipTypes.ThreeDimModel, absolutePath));
        }

        return new ThreeMfDocument(this._cts!, this._rs!, this._m);
    }
}
